import { EIntakeStep } from "@rentiohq/shared-frontend/dist/types/application.types";
import { EIntakeType } from "@rentiohq/shared-frontend/dist/types/broker.types";

export const getStepsForIntakeType = (
  intakeType: EIntakeType,
): EIntakeStep[][] => {
  switch (intakeType) {
    case EIntakeType.TwoPhases:
      return [
        [
          EIntakeStep.candidateVisitor,
          EIntakeStep.waitingList,
          EIntakeStep.invited,
          EIntakeStep.visitor,
        ],
        [EIntakeStep.candidateTenant, EIntakeStep.tenant],
        [EIntakeStep.notEligible],
      ];
    case EIntakeType.TwoPhasesWithVisit:
      return [
        [
          EIntakeStep.candidateVisitor,
          EIntakeStep.waitingList,
          EIntakeStep.planAsVisitor,
          EIntakeStep.invited,
          EIntakeStep.visitor,
        ],
        [EIntakeStep.candidateTenant, EIntakeStep.tenant],
        [EIntakeStep.notEligible],
      ];
    default:
      return [
        [EIntakeStep.candidateTenant],
        [EIntakeStep.tenant],
        [EIntakeStep.notEligible],
      ];
  }
};

export const getPhaseForStep = (intakeType: EIntakeType, step: EIntakeStep) => {
  const steps = getStepsForIntakeType(intakeType);
  return steps.findIndex(steps => steps.includes(step));
};

export interface IFlowItem {
  id: EIntakeStep;
  allowedSteps: EIntakeStep[];
}

export const getFlows = (
  intakeType: EIntakeType,
  hasCalendarSync?: boolean,
  hasRentMatch?: boolean,
): {
  visitorSteps?: IFlowItem[];
  candidateSteps: IFlowItem[];
  notEligibleSteps: IFlowItem[];
} => {
  const flows = {
    [EIntakeType.OnePhase]: {
      candidateSteps: [
        {
          id: EIntakeStep.candidateTenant,
          allowedSteps: [EIntakeStep.tenant, EIntakeStep.notEligible],
        },
        { id: EIntakeStep.tenant, allowedSteps: [EIntakeStep.notEligible] },
      ],
      notEligibleSteps: [
        {
          id: EIntakeStep.notEligible,
          allowedSteps: [
            EIntakeStep.candidateTenant,
            EIntakeStep.candidateVisitor,
            EIntakeStep.tenant,
          ],
        },
      ],
    },
    [EIntakeType.TwoPhases]: {
      visitorSteps: [
        {
          id: EIntakeStep.candidateVisitor,
          allowedSteps: [
            EIntakeStep.visitor,
            EIntakeStep.waitingList,
            EIntakeStep.invited,
            EIntakeStep.candidateTenant,
            EIntakeStep.notEligible,
          ],
        },
        {
          id: EIntakeStep.waitingList,
          allowedSteps: [
            EIntakeStep.candidateVisitor,
            EIntakeStep.visitor,
            EIntakeStep.invited,
            EIntakeStep.candidateTenant,
            EIntakeStep.notEligible,
          ],
        },
        {
          id: EIntakeStep.invited,
          allowedSteps: [
            EIntakeStep.candidateVisitor,
            EIntakeStep.waitingList,
            EIntakeStep.visitor,
            EIntakeStep.candidateTenant,
            EIntakeStep.notEligible,
          ],
        },
        {
          id: EIntakeStep.visitor,
          allowedSteps: [
            EIntakeStep.candidateVisitor,
            EIntakeStep.waitingList,
            EIntakeStep.invited,
            EIntakeStep.candidateTenant,
            EIntakeStep.notEligible,
          ],
        },
      ],
      candidateSteps: [
        {
          id: EIntakeStep.candidateTenant,
          allowedSteps: [EIntakeStep.tenant, EIntakeStep.notEligible],
        },
        { id: EIntakeStep.tenant, allowedSteps: [EIntakeStep.notEligible] },
      ],
      notEligibleSteps: [
        {
          id: EIntakeStep.notEligible,
          allowedSteps: [
            EIntakeStep.candidateVisitor,
            EIntakeStep.waitingList,
            EIntakeStep.invited,
            EIntakeStep.tenant,
          ],
        },
      ],
    },
    [EIntakeType.TwoPhasesWithVisit]: {
      visitorSteps: [
        {
          id: EIntakeStep.candidateVisitor,
          allowedSteps: [
            EIntakeStep.visitor,
            EIntakeStep.waitingList,
            EIntakeStep.planAsVisitor,
            EIntakeStep.invited,
            EIntakeStep.candidateTenant,
            EIntakeStep.notEligible,
          ],
        },
        {
          id: EIntakeStep.waitingList,
          allowedSteps: [
            EIntakeStep.visitor,
            EIntakeStep.waitingList,
            EIntakeStep.planAsVisitor,
            EIntakeStep.invited,
            EIntakeStep.candidateTenant,
            EIntakeStep.notEligible,
          ],
        },
        {
          id: EIntakeStep.planAsVisitor,
          allowedSteps: [
            EIntakeStep.candidateVisitor,
            EIntakeStep.waitingList,
            EIntakeStep.invited,
            EIntakeStep.visitor,
            EIntakeStep.candidateTenant,
            EIntakeStep.notEligible,
          ],
        },
        {
          id: EIntakeStep.invited,
          allowedSteps: [
            EIntakeStep.candidateVisitor,
            EIntakeStep.waitingList,
            EIntakeStep.planAsVisitor,
            EIntakeStep.visitor,
            EIntakeStep.candidateTenant,
            EIntakeStep.notEligible,
          ],
        },
        {
          id: EIntakeStep.visitor,
          allowedSteps: [
            EIntakeStep.candidateVisitor,
            EIntakeStep.waitingList,
            EIntakeStep.planAsVisitor,
            EIntakeStep.invited,
            EIntakeStep.candidateTenant,
            EIntakeStep.notEligible,
          ],
        },
      ],
      candidateSteps: [
        {
          id: EIntakeStep.candidateTenant,
          allowedSteps: [EIntakeStep.tenant, EIntakeStep.notEligible],
        },
        { id: EIntakeStep.tenant, allowedSteps: [EIntakeStep.notEligible] },
      ],
      notEligibleSteps: [
        {
          id: EIntakeStep.notEligible,
          allowedSteps: [
            EIntakeStep.candidateVisitor,
            EIntakeStep.waitingList,
            EIntakeStep.tenant,
          ],
        },
      ],
    },
  };

  const selectedFlow = flows[intakeType];

  if (hasCalendarSync === false || hasRentMatch === false) {
    const filterStep = (step: EIntakeStep) => {
      let include = true;
      if (hasCalendarSync === false) {
        include = include && step !== EIntakeStep.invited;
      }
      if (hasRentMatch === false) {
        include = include && step !== EIntakeStep.invited;
      }
      return include;
    };

    const filterSteps = (stepsArray?: IFlowItem[]) => {
      if (!stepsArray) return undefined;
      return stepsArray
        .filter(item => filterStep(item.id))
        .map(item => ({
          ...item,
          allowedSteps: item.allowedSteps.filter(filterStep),
        }));
    };

    return {
      visitorSteps: filterSteps((selectedFlow as any).visitorSteps),
      candidateSteps: filterSteps(selectedFlow.candidateSteps) || [],
      notEligibleSteps: filterSteps(selectedFlow.notEligibleSteps) || [],
    };
  }

  return selectedFlow;
};
